// src/components/dashboard/DashboardDetectionTrace.tsx
//
// Step 3 (continued) — fixes a real usability gap surfaced live: the
// findings list said "needs review" and linked here, but this page had
// no way to actually resolve anything — forcing a bounce back to a
// different page/section to act on what was just read. Now reuses
// ResolveFindingInline (same component already built and working in
// DashboardDetectionFindings.tsx) directly on each flagged event row,
// so review and resolution happen in one place.
//
// EventRow needs outcomeId (not previously required) to pass through
// to ResolveFindingInline's resolve call — threaded down from the
// page-level outcomeId already read from search params.
//
// FIXED (prior pass) — stale "flagged" state after resolution. isFlagged
// was previously the only signal driving both the badge and whether the
// Resolve button rendered, and it read purely from
// event.result.recommendation — a value frozen at classification time
// that never changes, even after the underlying finding is resolved.
// A resolved finding kept showing "flagged" with a live Resolve button
// indefinitely. get-detection-trace.ts now returns a live
// findingResolved flag per event (joined against the run's own findings
// subcollection, the same doc resolve-finding.ts writes to) — this file
// consumes it below instead of inferring resolution state from
// classification-time data alone.
//
// FIXED (this pass) — GSC support. traceTypes.ts's transaction field is
// now a discriminated union ('marqeta_auth_clearing' | 'gsc_metric'),
// not exclusively Marqeta-shaped — run-gsc-search-performance-detection.ts
// now writes real trace events too, and this page previously had no way
// to render them: it unconditionally read t.authAmount/clearedAmount,
// which don't exist on a GSC event's transaction object at all. Without
// this fix, a GSC run's trace page would either render nothing useful
// (falling into the discrepancyType/severity-only fallback) or, worse,
// silently show undefined/NaN if the shapes were ever conflated. Branch
// added on t.kind, mirroring the backend's own discriminant exactly.
// baseline also gains optional confidence/lookbackPeriods/windowStart/
// windowEnd — populated only by GSC events; Marqeta events correctly
// omit them, rendered defensively below rather than assumed present.

import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { authedFetch } from '../../services/dashboardApi';
import { ArrowLeft, Loader2Icon } from 'lucide-react';

type TraceEvent =
  | { type: 'classification_started'; transactionId: string; timestamp: string }
  | {
      type: 'classification_succeeded';
      transactionId: string;
      timestamp: string;
      findingResolved: boolean;
      result: {
        discrepancyType: string | null;
        severity: 'none' | 'minor' | 'material';
        recommendation: 'auto_complete' | 'escalate';
        triggeredRuleId: string | null;
      };
      transaction?:
        | {
            kind: 'marqeta_auth_clearing';
            mcc: string;
            mid: string;
            authAmount: number;
            clearedAmount: number;
            deltaPercent: number;
          }
        | {
            kind: 'gsc_metric';
            metric: 'clicks' | 'impressions' | 'ctr' | 'position';
            currentValue: number;
            baselineMean: number;
            baselineStdDev: number;
          };
      baseline?: {
        source: string;
        sampleSize: number;
        confidence?: 'low' | 'medium' | 'high';
        lookbackPeriods?: number;
        windowStart?: string | null;
        windowEnd?: string | null;
      };
    }
  | {
      type: 'classification_failed';
      transactionId: string;
      timestamp: string;
      reason: 'no_text_block' | 'parse_error';
      rawResponse: string | null;
    };

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function formatCurrency(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

function formatDelta(deltaPercent: number): string {
  const sign = deltaPercent >= 0 ? '+' : '';
  return `${sign}${deltaPercent.toFixed(1)}%`;
}

// Formats a raw GSC metric name for display — 'ctr' as an uppercase
// acronym, everything else capitalized normally.
function formatMetricName(metric: string): string {
  return metric === 'ctr' ? 'CTR' : metric.charAt(0).toUpperCase() + metric.slice(1);
}

function formatMetricValue(metric: string, value: number): string {
  if (metric === 'ctr') return `${(value * 100).toFixed(1)}%`;
  if (metric === 'position') return value.toFixed(1);
  return value.toFixed(metric === 'impressions' || metric === 'clicks' ? 0 : 2);
}

// ── Inline resolve — same shape/behavior as DashboardDetectionFindings.tsx's
// ResolveFindingInline. Duplicated rather than imported cross-file for
// now (both files are in the same dashboard/ directory; could be
// extracted to a shared component later if this duplication becomes a
// maintenance issue — flagging that as a deliberate, minor tradeoff,
// not an oversight).
function ResolveFindingInline({
  outcomeId,
  runId,
  transactionId,
  onResolved,
}: {
  outcomeId: string;
  runId: string;
  transactionId: string;
  onResolved: () => void;
}) {
  const [resolutionNote, setResolutionNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [resolved, setResolved] = useState(false);

  const handleSubmit = async () => {
    if (resolutionNote.trim().length < 1) {
      setError('Enter how this was resolved — a genuine account, not a placeholder.');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const res = await authedFetch('/detection/findings/resolve', {
        method: 'POST',
        body: JSON.stringify({ outcomeId, runId, transactionId, resolutionNote: resolutionNote.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to resolve finding.');
      setResolutionNote('');
      setOpen(false);
      setResolved(true);
      onResolved();
    } catch (err: any) {
      setError(err.message || 'Could not resolve this finding.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (resolved) {
    return <p className="mt-2 text-xs text-green-700">Resolved.</p>;
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="mt-2 rounded-lg border border-brand-border px-3 py-1.5 text-xs font-medium text-brand-charcoal hover:bg-brand-surface"
      >
        Resolve
      </button>
    );
  }

  return (
    <div className="mt-2 space-y-2 rounded-lg border border-brand-border bg-brand-surface p-3">
      <p className="text-xs text-brand-gray">
        e.g. "confirmed with vendor, approved" or "miscategorized, corrected to Office Supplies."
      </p>
      <textarea
        value={resolutionNote}
        onChange={(e) => setResolutionNote(e.target.value)}
        placeholder="How was this resolved?"
        rows={2}
        className="w-full resize-none rounded-lg border border-brand-border px-3 py-2 text-sm"
      />
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex justify-end gap-2">
        <button onClick={() => setOpen(false)} className="rounded-lg px-3 py-1.5 text-xs text-brand-charcoal hover:bg-brand-white">
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="rounded-lg bg-brand-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-accentHover disabled:opacity-50"
        >
          {isSubmitting ? <Loader2Icon className="inline size-3.5 animate-spin" /> : 'Confirm resolution'}
        </button>
      </div>
    </div>
  );
}

function EventRow({
  event,
  outcomeId,
  runId,
  onResolved,
}: {
  event: TraceEvent;
  outcomeId: string;
  runId: string;
  onResolved: () => void;
}) {
  if (event.type === 'classification_started') {
    return (
      <div className="flex items-center gap-3 py-2 text-sm">
        <span className="rounded-full px-2 py-0.5 text-xs font-mono bg-brand-surface text-brand-gray">started</span>
        <span className="font-mono text-xs text-brand-charcoal">{event.transactionId}</span>
        <span className="text-xs text-brand-gray ml-auto">{formatTime(event.timestamp)}</span>
      </div>
    );
  }

  if (event.type === 'classification_succeeded') {
    const isFlagged = event.result.recommendation === 'escalate';
    const isResolved = event.findingResolved;
    const t = event.transaction;
    const b = event.baseline;

    return (
      <div className="py-3 text-sm border-t border-brand-border first:border-t-0">
        <div className="flex items-center gap-3">
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-mono ${
              !isFlagged || isResolved ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}
          >
            {!isFlagged ? 'clear' : isResolved ? 'resolved' : 'flagged'}
          </span>
          <span className="font-mono text-xs text-brand-charcoal">{event.transactionId}</span>
          <span className="text-xs text-brand-gray ml-auto">{formatTime(event.timestamp)}</span>
        </div>

        {t?.kind === 'marqeta_auth_clearing' ? (
          <div className="mt-2 pl-1">
            <p className="text-sm text-brand-charcoal">
              Authorized <span className="font-medium text-brand-black">{formatCurrency(t.authAmount)}</span>, cleared at{' '}
              <span className="font-medium text-brand-black">{formatCurrency(t.clearedAmount)}</span>
              {' — '}
              <span className={isFlagged && !isResolved ? 'text-red-700 font-medium' : 'text-green-700 font-medium'}>
                {formatDelta(t.deltaPercent)}
              </span>
            </p>
            {b && (
              <p className="text-xs text-brand-gray mt-1">
                Checked against MCC {t.mcc}'s own history ({b.sampleSize} prior transaction{b.sampleSize === 1 ? '' : 's'} on file).
                {' '}
                {isFlagged
                  ? isResolved
                    ? 'This delta fell outside the normal range and was flagged — since resolved.'
                    : 'This delta fell outside the normal range, so it was flagged for review rather than closed automatically.'
                  : 'Within the normal range — closed automatically, no review needed.'}
              </p>
            )}
          </div>
        ) : t?.kind === 'gsc_metric' ? (
          <div className="mt-2 pl-1">
            <p className="text-sm text-brand-charcoal">
              {formatMetricName(t.metric)}:{' '}
              <span className="font-medium text-brand-black">{formatMetricValue(t.metric, t.currentValue)}</span>
              {' vs. historical average '}
              <span className="font-medium text-brand-black">{formatMetricValue(t.metric, t.baselineMean)}</span>
            </p>
            {b && (
              <p className="text-xs text-brand-gray mt-1">
                Checked against this page's own history
                {b.source === 'site' ? ' (site-wide fallback — this page lacks enough of its own)' : ''}
                {' '}({b.sampleSize} prior period{b.sampleSize === 1 ? '' : 's'}
                {b.windowStart && b.windowEnd ? `, ${b.windowStart} to ${b.windowEnd}` : ''}).
                {' '}
                {b.confidence === 'low' && (
                  <span className="text-amber-700">Low confidence — treat this finding as a weaker signal. </span>
                )}
                {isFlagged
                  ? isResolved
                    ? 'This value fell outside the normal range and was flagged — since resolved.'
                    : 'This value fell outside the normal range, so it was flagged for review rather than closed automatically.'
                  : 'Within the normal range — closed automatically, no review needed.'}
              </p>
            )}
          </div>
        ) : (
          <p className="text-xs text-brand-gray mt-1">
            {event.result.discrepancyType ?? 'No discrepancy'} · severity: {event.result.severity}
          </p>
        )}

        {/* Resolve button only for still-open flagged events. Resolved
            findings show a static confirmation instead — matches
            ResolveFindingInline's own post-submit state, but rendered
            immediately from load rather than waiting for a fresh submit
            in this session. */}
        {isFlagged && !isResolved && (
          <ResolveFindingInline
            outcomeId={outcomeId}
            runId={runId}
            transactionId={event.transactionId}
            onResolved={onResolved}
          />
        )}
        {isFlagged && isResolved && (
          <p className="mt-2 text-xs text-green-700">Resolved.</p>
        )}
      </div>
    );
  }

  return (
    <div className="py-2 text-sm border-t border-brand-border first:border-t-0">
      <div className="flex items-center gap-3">
        <span className="rounded-full px-2 py-0.5 text-xs font-mono bg-amber-50 text-amber-700">failed</span>
        <span className="font-mono text-xs text-brand-charcoal">{event.transactionId}</span>
        <span className="text-xs text-brand-gray ml-auto">{formatTime(event.timestamp)}</span>
      </div>
      <p className="text-xs text-amber-700 mt-1">Reason: {event.reason}</p>
    </div>
  );
}

export const DashboardDetectionTrace: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const [searchParams] = useSearchParams();
  const outcomeId = searchParams.get('outcomeId');

  const [events, setEvents] = useState<TraceEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!runId || !outcomeId) {
      setError('Missing runId or outcomeId.');
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const res = await authedFetch(`/detection/runs/${encodeURIComponent(runId)}/trace?outcomeId=${encodeURIComponent(outcomeId)}`);
      if (!res.ok) throw new Error('Failed to load run trace.');
      const data = await res.json();
      setEvents(data.events ?? []);
    } catch (err: any) {
      setError(err.message || 'Could not load this run\'s trace.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, [runId, outcomeId]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1.5 text-sm text-brand-gray">
        <Link to="/dashboard/detection" className="hover:text-brand-black hover:underline">
          Detection
        </Link>
        <span>/</span>
        <span className="text-brand-black font-medium">Run trace</span>
      </div>

      <div>
        <h1 className="text-lg font-semibold text-brand-black">Run trace</h1>
        <p className="text-sm text-brand-gray mt-1">
          Every transaction checked in this run, and the reasoning behind each result. Flagged items can be resolved right here.
        </p>
      </div>

      {isLoading && <p className="text-sm text-brand-gray font-mono">Loading…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {!isLoading && !error && events.length === 0 && (
        <div className="rounded-xl border border-dashed border-brand-border p-6 text-center">
          <p className="text-sm text-brand-gray">No trace events found for this run.</p>
        </div>
      )}

      {events.length > 0 && runId && outcomeId && (
        <div className="rounded-xl border border-brand-border bg-brand-white p-4">
          {events.map((e, i) => (
            <EventRow key={i} event={e} outcomeId={outcomeId} runId={runId} onResolved={load} />
          ))}
        </div>
      )}
    </div>
  );
};